import type React from "react";

import { useRouter } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";

import { ConfirmationModal } from "@/components/confirmation-modal";
import { toast } from "@/components/toast/toast-queue.ts";
import { useDeleteRequest } from "@/features/request/edit/hooks/useDeleteRequest";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";

interface SubmitWarningModalProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenUnlock: () => void;
}

export function DeleteRequestModal({
  isOpen,
  onOpenChange,
  onOpenUnlock,
}: SubmitWarningModalProps) {
  const { isPending, mutate: deleteRequest } = useDeleteRequest();
  const router = useRouter();
  const { isTemplate } = useIsTemplate();

  const handleDelete = () => {
    deleteRequest(undefined, {
      onError: () => {
        toast.add({
          description:
            "There was an error deleting the request. Please try again.",
          timeout: 2000,
          title: "Error deleting request",
          type: "error",
        });
      },
      onSuccess: () => {
        toast.add({
          description: "The request has been successfully deleted.",
          timeout: 2000,
          title: "Request deleted",
          type: "success",
        });
        onOpenChange(false);
        router.history.go(-1);
      },
    });
  };

  return (
    <ConfirmationModal
      cancelButtonText="Nevermind"
      confirmButtonColor="destructive"
      confirmButtonText={isPending ? "Deleting..." : "Delete"}
      icon={<Trash2 className="text-featured-icon-light-fg-error" />}
      isConfirmLoading={isPending}
      isOpen={isOpen}
      onConfirm={
        isTemplate
          ? () => {
              onOpenUnlock();
              onOpenChange(false);
            }
          : handleDelete
      }
      onOpenChange={onOpenChange}
      text={
        <>
          <p className="text-sm text-text-tertiary">
            You are about to delete this request from your list of requests.
          </p>
          <p className="text-sm text-text-tertiary">
            Deleting this will remove all details and assets attached to the
            request. Are you sure you want to delete?
          </p>
        </>
      }
      title="Delete Request"
    />
  );
}
